// apps/cli/src/commands/prereqs.ts
import { Command, Option } from "clipanion";
import { M8tCommand } from "../lib/m8t-command.js";
import { LocalCliError } from "../lib/errors.js";
import { colors, renderJson, resolveOutputMode } from "../lib/output.js";
import { buildPrereqDeps, INSTALL_REASONING_MODEL } from "../lib/prereq-deps.js";
import { runPrereqs, type PrereqRunOptions } from "../lib/prereq-run.js";
import { prereqsForPhase, type PrereqPhase, type PrereqVerdict } from "../lib/prereqs.js";

const GLYPH: Record<string, string> = {
  pass: colors.success("PASS"),
  fixed: colors.success("FIXED"),
  warn: colors.hint("WARN"),
  fail: colors.error("FAIL"),
  skipped: colors.dim("SKIP"),
};

export function renderVerdict(v: PrereqVerdict): string {
  const lines: string[] = [];
  lines.push(
    v.phase === "install"
      ? colors.field("Checking whether this subscription can install m8t.")
      : colors.field("Checking whether you can use this m8t platform."),
  );
  lines.push("");
  for (const r of v.results) {
    lines.push(`[${GLYPH[r.status]}] ${r.title}`);
    lines.push(`        ${colors.dim(r.detail)}`);
    if (r.remedy) lines.push(colors.hint(`        fix: ${r.remedy}`));
  }
  if (v.unchecked.length > 0) {
    lines.push("");
    lines.push(
      colors.dim(
        `Not checked by this command (you may still hit them): ${v.unchecked.join(", ")}. ` +
          `See guides/prerequisites.md.`,
      ),
    );
  }
  lines.push("");
  lines.push(v.ok ? colors.success("✅ Nothing is blocking you.") : colors.error("⛔ Blocked — fix the FAIL rows above, then re-run."));
  return lines.join("\n");
}

export class PrereqsCommand extends M8tCommand {
  static paths = [["prereqs"]];
  static usage = Command.Usage({
    description: "Check (and optionally fix) everything m8t needs — to install, or for you to use it.",
    details:
      "Runs one of two phases. With no platform discoverable it checks INSTALL prerequisites: your Azure sign-in, subscription and directory rights, resource-provider registrations, soft-deleted Cognitive Services accounts (they hold their model quota until purged), and model quota. With a platform live it checks USAGE prerequisites for the signed-in account: the sign-in redirect URI, Foundry data-plane access, and Key Vault secrets access. Pass --fix to repair what is repairable, and --fix --for <upn> to set a teammate up (usage phase only). Read-only without --fix.",
    examples: [
      ["Before installing", "$0 prereqs"],
      ["Register anything missing, then install", "$0 prereqs --fix"],
      ["After joining a platform someone else installed", "$0 prereqs"],
      ["Grant yourself what you are missing", "$0 prereqs --fix"],
      ["Set a teammate up (needs role-assignment rights)", "$0 prereqs --fix --for alice@contoso.com"],
    ],
  });

  fix = Option.Boolean("--fix", false, { description: "Repair what is repairable. Without it the command only reports." });
  for_ = Option.String("--for", { description: "UPN or object id of another person. Usage phase only." });
  phase = Option.String("--phase", { description: "Force 'install' or 'usage' instead of auto-detecting." });
  region = Option.String("--region", { description: "Target region. Enables the install-phase quota check, and judges soft-deleted accounts against the region you will install into (without it, they only warn)." });
  model = Option.String("--model", { description: `Model whose quota the install needs (default ${INSTALL_REASONING_MODEL}).` });
  clientId = Option.String("--client-id", { description: "A ready app registration, in place of directory-admin rights." });
  subscription = Option.String("--subscription");
  resourceGroup = Option.String("--resource-group");
  output = Option.String("--output");

  async executeCommand(): Promise<number> {
    const mode = resolveOutputMode(this.output as "pretty" | "json" | "auto" | undefined, this.context.stdout as NodeJS.WriteStream);
    const json = mode === "json";

    const phase = this.resolvePhase();
    const forPrincipal = typeof this.for_ === "string" ? this.for_ : undefined;

    if (forPrincipal && phase === "install") {
      throw new LocalCliError({
        code: "PREREQS_FOR_INSTALL_PHASE",
        message: "--for applies to the usage phase only.",
        hint: "Install prerequisites belong to the subscription and the person running the install, not to a named teammate. Run 'm8t prereqs --fix --for <upn>' once the platform is live.",
      });
    }
    if (forPrincipal && !this.fix) {
      // Checking someone else read-only is legitimate; say what evidence it uses.
      this.context.stderr.write(
        colors.dim("  (checking by role assignment — we cannot call the data plane as someone else)\n"),
      );
    }

    const deps = buildPrereqDeps({
      resourceGroup: typeof this.resourceGroup === "string" ? this.resourceGroup : undefined,
      subscription: typeof this.subscription === "string" ? this.subscription : undefined,
    });

    const opts: PrereqRunOptions & { phase?: PrereqPhase } = {
      fix: this.fix === true,
      ...(forPrincipal ? { forPrincipal } : {}),
      ...(phase ? { phase } : {}),
      region: typeof this.region === "string" ? this.region : undefined,
      model: typeof this.model === "string" ? this.model : INSTALL_REASONING_MODEL,
      clientId: typeof this.clientId === "string" ? this.clientId : undefined,
      subscription: typeof this.subscription === "string" ? this.subscription : undefined,
      resourceGroup: typeof this.resourceGroup === "string" ? this.resourceGroup : undefined,
      onProgress: json ? undefined : (m) => this.context.stderr.write(colors.dim(`  ${m}\n`)),
    };

    const verdict = await runPrereqs(deps, opts);

    if (json) {
      this.context.stdout.write(renderJson(verdict) + "\n");
    } else {
      this.context.stdout.write(renderVerdict(verdict) + "\n");
    }
    return verdict.ok ? 0 : 1;
  }

  private resolvePhase(): PrereqPhase | undefined {
    if (typeof this.phase !== "string") return undefined;
    if (this.phase !== "install" && this.phase !== "usage") {
      throw new LocalCliError({
        code: "PREREQS_BAD_PHASE",
        message: `Unknown phase '${this.phase}'.`,
        hint: `Valid phases: install, usage. Omit --phase to auto-detect (install prerequisites are ${prereqsForPhase("install").length.toString()} checks; usage are ${prereqsForPhase("usage").length.toString()}).`,
      });
    }
    return this.phase;
  }
}
